import * as React from "react";
import GoogleMapReact from "google-map-react";
import { fitBounds } from 'google-map-react/utils';
import LatLngBounds from 'google-map-react/lib/utils/lib_geo/lat_lng_bounds.js';
import LatLng from 'google-map-react/lib/utils/lib_geo/lat_lng.js';
import Supercluster from 'points-cluster';
import Marker from "./components/Marker";
import ClusterMarker from "./components/ClusterMarker";
import { mapStyle } from "src/share/utils/mapStyle";
import { IMapCenter } from "src/share/types/common";
import "../Map.css";
import { getZoom } from "src/share/utils/utils";

export enum MapClustersTypes {
  GEOSEARCH = 'Geosearch',
  ALERTS = 'Alerts'
}
export interface IMapClusters {
  id: string;
  lat: number;
  lng: number;
  radius: number;
  caseId: string;
  date: number;
  person?: {
    personId: string;
    fullName: string;
  };
  manual?: boolean;
  searchName?: string;
  location?: string;
  expirationDate?: number;
  expired?: boolean;
  stop?: string;
}
export interface IMapProps {
  clusters: IMapClusters[];
  mapType: MapClustersTypes;
  history: any;
}
export interface IMapState {
  clusters: any[];
  mapOptions: {
    center: IMapCenter,
    zoom: number,
    bounds: any
  },
}
const defaultProps = {
  center: {
    lat: 9.928069,
    lng: -84.090729
  },
  zoom: 0,
  bounds: {
    nw: 0,
    se: 0
  }
};
export default class MapClusters extends React.PureComponent<IMapProps, IMapState> {
  public constructor(props) {
    super(props);
    this.state = {
      clusters: [],
      mapOptions: {
        ...defaultProps
      },
    };
  }
  public componentDidUpdate(prevProps: IMapProps) {
    if (prevProps.clusters !== this.props.clusters) {
      this.createClusters(this.props);
      if (this.props.clusters.length > 1) {
        this.centerBoundsPoints(this.props.clusters, true);
      } else if (this.props.clusters.length === 1) {
        this.setBoundsPoints(this.props.clusters[0], 10, defaultProps.bounds);
      }
    }
  }
  public getClusters = (props: IMapProps) => {
    const clusters = new Supercluster(props.clusters, {
      minZoom: 0,
      maxZoom: 20,
      radius: 100
    });
    return clusters(this.state.mapOptions);
  };
  public createClusters = (props: IMapProps) => {
    this.setState({
      clusters: this.state.mapOptions.bounds
        ? this.getClusters(props).map(({ wx, wy, numPoints, points }) => ({
          lat: wy,
          lng: wx,
          numPoints,
          radius: (this.props.clusters.find(cluster => cluster.id === points[0].id) as IMapClusters).radius,
          id: `${numPoints}_${points[0].id}`,
          points,
          personType: points[0].personType
        }))
        : []
    });
  };
  public handleMapChange = ({ center, zoom, bounds }) => {
    this.setState(
      {
        mapOptions: {
          center,
          zoom,
          bounds
        }
      },
      () => {
        this.createClusters(this.props);
      }
    );
  };
  public getMapSize() {
    const map = document.getElementById('map-clusters');
    return map ? { width: map.offsetWidth, height: map.offsetHeight } : undefined;
  }
  public getMapBounds = (points: IMapClusters[]) => {
    const bounds = new LatLngBounds();
    const locations = points.map(point => ({ latitude: point.lat, longitude: point.lng }));
    locations.forEach((location) => {
      bounds.extend(new LatLng(location.latitude, location.longitude));
    });
    return bounds;
  }
  public getCenterBounds(points: IMapClusters[]) {
    const { _ne: ne, _sw: sw, _nw: nw, _se: se } = this.getMapBounds(points);
    const locations = ne && sw ? { ne, sw } : nw && se ? { nw, se } : undefined;
    const size = this.getMapSize();
    if (locations && size) {
      const fit = fitBounds(locations, size);
      return fit;
    }
    return undefined;
  }
  public centerBoundsPoints = (points: IMapClusters[], initial: boolean = false) => {
    const locations = this.checkPoints(points);
    const isAlertClusterMap = this.props.mapType === MapClustersTypes.ALERTS;
    if (locations.length > 1) {
      const { center, zoom, newBounds } = this.getCenterBounds(points);
      if (center && zoom && newBounds) {
        const maxRadius = Math.max(...points.map((o) => o.radius));
        const radiusZoom = getZoom(maxRadius);
        const pointsZoom = isAlertClusterMap ? (initial ? (radiusZoom - 4) : radiusZoom) : (zoom - 1);
        this.setBoundsPoints(center, pointsZoom, newBounds);
      }
    } else if (locations.length > 0) {
      const pointsZoom = isAlertClusterMap ? (getZoom(points[0].radius) + 1) : 10;
      this.setBoundsPoints(points[0], pointsZoom, defaultProps.bounds)
    }
  }
  public setBoundsPoints = (center: IMapCenter, zoom: number, bounds: any) => {
    this.setState({
      mapOptions: {
        center, zoom, bounds
      }
    });
  }
  public checkPoints(points: IMapClusters[]) {
    return points.reduce((pointsList, point) => {
      const exist = pointsList.find(pointSaved => pointSaved && point && pointSaved.lat === point.lat && pointSaved.lng === point.lng);
      if (!exist) {
        pointsList = [...pointsList, point];
      }
      return pointsList;
    }, [] as IMapClusters[]);
  }
  public render() {
    const isAlertClusterMap = this.props.mapType === MapClustersTypes.ALERTS;
    return (
      <div id="map-clusters" className="map">
        <GoogleMapReact
          options={{
            zoomControl: false,
            fullscreenControl: false,
            styles: mapStyle
          }}
          defaultZoom={defaultProps.zoom}
          defaultCenter={defaultProps.center}
          onChange={this.handleMapChange}
          bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_KEY || "" }}
          center={this.state.mapOptions.center}
          zoom={this.state.mapOptions.zoom}
        >
          {this.state.clusters.map(item => this.checkPoints(item.points).length === 1 ?
            <Marker
              key={item.id}
              id={item.id}
              mapType={this.props.mapType}
              points={item.points}
              lat={item.lat}
              lng={item.lng}
              radius={item.radius}
              zoom={this.state.mapOptions.zoom}
              centerBoundsPoints={() => this.setBoundsPoints(item.points[0], isAlertClusterMap ? (getZoom(item.radius) + 2) : 12, defaultProps.bounds)}
              history={this.props.history}
            /> :
            <ClusterMarker
              key={item.id}
              lat={item.lat}
              lng={item.lng}
              mapType={this.props.mapType}
              zoom={this.state.mapOptions.zoom}
              radius={item.radius}
              points={item.points}
              centerBoundsPoints={() => this.centerBoundsPoints(item.points)}
              history={this.props.history}
            />
          )}
        </GoogleMapReact>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/share/components/Maps/MapClusters/MapClusters.tsx